/**
 * Formal meeting end helpers: adjournment detection, archive, end live session.
 */

import { saveMeetingArchive } from './meetingArchives';
import { endMeetingSession } from './officerSession';
import { hasSupabaseConfig } from '../lib/supabaseClient';

export function minutesIncludeAdjournment(minutes) {
  if (!Array.isArray(minutes) || !minutes.length) return false;
  return minutes.some((e) => {
    if (!e) return false;
    if (e.type === 'adjournment' || e.effectType === 'adjourn') return true;
    if (e.motionData?.type === 'ADJ') return true;
    const t = String(e.text || '').toLowerCase();
    return t.includes('meeting adjourned') || t.includes('motion to adjourn');
  });
}

export function isAdjournMotion(motion) {
  if (!motion) return false;
  if (motion.type === 'ADJ') return true;
  return /\badjourn\b/i.test(String(motion.text || ''));
}

export function textLooksLikeAdjourn(text) {
  return /\badjourn\b/i.test(String(text || ''));
}

/**
 * Build a formal adjournment minute entry (caller appends / addMinuteEntry).
 */
export function createAdjournmentMinuteEntry({ meetingStartTime = null, chronologicalOrder = null } = {}) {
  const endTime = new Date();
  return {
    id: `adjournment_${endTime.getTime()}`,
    text: `Meeting adjourned at ${endTime.toLocaleTimeString()}`,
    type: 'adjournment',
    timestamp: endTime.toLocaleTimeString(),
    chronologicalOrder: chronologicalOrder != null ? chronologicalOrder : Date.now(),
    status: 'completed',
    metadata: {
      fullDateTime: endTime.toISOString(),
      meetingDuration: meetingStartTime
        ? Math.round((endTime - new Date(meetingStartTime)) / 60000)
        : null,
    },
  };
}

/**
 * Persist Meeting Log + optionally end the officer session.
 * By default does not end the session if archiving fails (caller can confirm, then end).
 * @returns {{ archiveError: Error|null, endedSession: boolean }}
 */
export async function archiveAndEndLiveSession({
  organizationId,
  meetingSessionId = null,
  meetingMinutes = [],
  meetingSetup = {},
  meetingStartTime = null,
  meetingEndTime = null,
  meetingTimer = 0,
  endSession = true,
  endSessionEvenIfArchiveFails = false,
} = {}) {
  let archiveError = null;
  const entries = Array.isArray(meetingMinutes) ? meetingMinutes : [];

  if (hasSupabaseConfig() && organizationId && entries.length > 0) {
    try {
      await saveMeetingArchive({
        organizationId,
        meetingSessionId,
        meetingMinutes: entries,
        meetingSetup,
        meetingStartTime,
        meetingEndTime: meetingEndTime || new Date().toISOString(),
        meetingTimer,
      });
    } catch (err) {
      archiveError = err instanceof Error ? err : new Error(String(err?.message || err));
    }
  }

  let endedSession = false;
  const shouldEnd =
    endSession &&
    hasSupabaseConfig() &&
    meetingSessionId &&
    (!archiveError || endSessionEvenIfArchiveFails || entries.length === 0);

  if (shouldEnd) {
    try {
      await endMeetingSession(meetingSessionId);
      endedSession = true;
    } catch (err) {
      console.warn('[finalizeMeeting] endMeetingSession failed', err?.message || err);
    }
  }

  return { archiveError, endedSession };
}

/**
 * Build archive payload from a live meeting_sessions.state jsonb blob (Org Home path).
 */
export function archivePayloadFromSessionState(state, { organizationId, meetingSessionId }) {
  const s = state && typeof state === 'object' ? state : {};
  const meetingSetup = s.meetingSetup || {};
  return {
    organizationId,
    meetingSessionId,
    meetingMinutes: Array.isArray(s.meetingMinutes) ? s.meetingMinutes : [],
    meetingSetup,
    meetingStartTime: s.meetingStartTime || meetingSetup.meetingStartTime || null,
    meetingEndTime: s.meetingEndTime || meetingSetup.meetingEndTime || new Date().toISOString(),
    meetingTimer: typeof s.meetingTimer === 'number' ? s.meetingTimer : 0,
  };
}
